/* eslint-disable fp/no-mutation */
import dayjs from 'dayjs'
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Paper,
  Snackbar,
  TextField,
  Typography,
} from '@mui/material'
import Grid from '@mui/material/Grid'
import ViewTimelineIcon from '@mui/icons-material/ViewTimeline'
import { useEffect, useState } from 'react'
import { useAppDispatch, useAppSelector } from 'src/store/hooks'
import axios from 'src/utilities/http-client'
import withConfirm from 'src/components/common/with-confirm'
import { always, compose, pipe } from 'ramda'
import TimelineItem from '@mui/lab/TimelineItem'
import TimelineOppositeContent from '@mui/lab/TimelineOppositeContent'
import TimelineSeparator from '@mui/lab/TimelineSeparator'
import TimelineDot from '@mui/lab/TimelineDot'
import TimelineConnector from '@mui/lab/TimelineConnector'
import TimelineContent from '@mui/lab/TimelineContent'
import Timeline from '@mui/lab/Timeline'
import Subheader from 'src/v3/components/subheader'
import {
  fetchClasses,
  fetchLinkedServices,
  selectUserQuizEventsByUserID,
  selectClassEventsByUserId,
  selectClassesByClassID,
  selectClassesStatus,
  selectLinkedServicesStatus,
  selectUserByEmail,
  selectUserCertificatesByUserId,
  fetchUserClassEvents,
  selectRolesStatus,
  fetchRoles,
  selectAccessLevelsStatus,
  fetchAccessLevels,
  selectCourses,
  selectCourseByID,
  selectCoursesStatus,
  fetchCourses,
  selectCourseEventsByUserId,
  selectClassesByCourseID,
  selectAllVideos,
  selectVideoByID,
} from '../admin.slice'

let timeLineStyle = {
  background: 'radial-gradient(rgb(66, 165, 245,0.4) 0%, rgba(0, 0, 0,0) 60%)',
  backgroundSize: 'cover',
  maxHeight: 600,
  overflow: 'scroll',
}

export default ({ selectedUserEmail }: { selectedUserEmail: string }) => {
  let dispatch = useAppDispatch()

  let [errorMessage, setErrorMessage] = useState<string>('')
  let accessLevelsStatus = useAppSelector(selectAccessLevelsStatus)
  let [modalStatus, setModalStatus] = useState<
    'None' | 'CompleteClass' | 'CompleteVideo'
  >('None')
  let [selectedCourse, setSelectedCourse] = useState<string>('')

  let user = useAppSelector(selectUserByEmail(selectedUserEmail))
  let rolesStatus = useAppSelector(selectRolesStatus)
  let linkedServicesStatus = useAppSelector(selectLinkedServicesStatus)
  let classesStatus = useAppSelector(selectClassesStatus)
  let coursesStatus = useAppSelector(selectCoursesStatus)
  let courses = useAppSelector(selectCourses)
  let course = useAppSelector(selectCourseByID(selectedCourse))
  let classes = useAppSelector(selectClassesByCourseID(course?.id ?? ''))
  let certificates = useAppSelector(
    selectUserCertificatesByUserId(user?.id ?? ''),
  )
  let quizResults = useAppSelector(selectUserQuizEventsByUserID(user?.id ?? ''))
  let classEvents = useAppSelector(selectClassEventsByUserId(user?.id ?? ''))
  let courseEvents = useAppSelector(selectCourseEventsByUserId(user?.id ?? ''))
  let [selectedClassID, setSelectedClassID] = useState<string>('')
  let $class = useAppSelector(selectClassesByClassID(selectedClassID))
  let [selectedTimeline, setSelectedTimeline] = useState<
    'None' | 'Certificates' | 'CourseEvents' | 'ClassEvents' | 'QuizResults'
  >('None')
  let videos = useAppSelector(selectAllVideos)
  let [selectedVideoID, setSelectedVideoID] = useState<string>('')
  let video = useAppSelector(selectVideoByID(selectedVideoID))

  useEffect(() => {
    if (linkedServicesStatus === 'uninitialized') {
      dispatch(fetchLinkedServices())
    }
  }, [linkedServicesStatus])

  useEffect(() => {
    if (classesStatus === 'uninitialized') {
      dispatch(fetchClasses())
    }
  }, [classesStatus])

  useEffect(() => {
    if (coursesStatus === 'uninitialized') {
      dispatch(fetchCourses())
    }
  }, [coursesStatus])

  useEffect(() => {
    if (rolesStatus === 'uninitialized') {
      dispatch(fetchRoles())
    }
  }, [rolesStatus])

  useEffect(() => {
    if (accessLevelsStatus === 'uninitialized') {
      dispatch(fetchAccessLevels())
    }
  }, [accessLevelsStatus])

  let postCompleteClass = () => {
    if (course?.id) {
      axios
        .post('/api/v2/admin/stream-event/class', {
          userID: user?.id,
          classId: $class?.id,
          status: 'completed',
        })
        .then(() => dispatch(fetchUserClassEvents([user?.id ?? ''])))
        .catch((err) => {
          setErrorMessage(err?.response?.data)
        })
    }
  }

  let postCompleteVideo = () => {
    if (video?.id) {
      axios
        .post('/api/v2/admin/stream-event/video', {
          userID: user?.id,
          videoID: video?.id,
          status: 'completed',
        })
        .then(() => setSelectedVideoID(''))
        .catch((err) => {
          setErrorMessage(err?.response?.data)
        })
    }
  }

  let ConfirmCompleteClassForUser = withConfirm(
    'Complete Class For User',
    `With great power comes great responsibility. Are you sure you want to complete ${$class?.name} for ${user?.firstName} ${user?.lastName}?`,
    modalStatus === 'CompleteClass',
    compose(setModalStatus, always('None') as any),
    pipe(postCompleteClass, compose(setModalStatus, always('None') as any)),
  )

  let ConfirmCompleteVideoForUser = withConfirm(
    'Complete Video For User',
    `With great power comes great responsibility. Are you sure you want to complete ${video?.name} for ${user?.firstName} ${user?.lastName}?`,
    modalStatus === 'CompleteVideo',
    compose(setModalStatus, always('None') as any),
    pipe(postCompleteVideo, compose(setModalStatus, always('None') as any)),
  )

  return (
    <>
      <>
        <Grid container spacing={2}>
          <Grid xs={12}>
            <Autocomplete
              className="mx-auto my-4"
              isOptionEqualToValue={(option, value) => option.id === value.id}
              getOptionLabel={(option) => `${option.name}`}
              onChange={(_, value) => {
                setSelectedCourse(value?.id ?? '')
              }}
              disablePortal
              options={courses}
              renderInput={(params) => (
                <TextField {...params} label="All Courses" />
              )}
            />
          </Grid>
          {!!course && (
            <Grid xs={12}>
              <Paper
                sx={{
                  p: 2,
                  margin: 'auto',
                  flexGrow: 1,
                }}
                style={{
                  backgroundImage: `url(${course?.imageName})`,
                  backgroundSize: 'cover',
                  backgroundPosition: '0px 48%',
                  boxShadow: 'inset 0 0 0 1000px rgba(37, 43, 66, 0.94)',
                }}
              >
                <Grid container>
                  <Grid xs={12} md={6}>
                    <Typography
                      variant="h6"
                      color="text.secondary"
                      gutterBottom
                      className="w-full"
                    >
                      {course?.name}
                    </Typography>
                    <Typography
                      variant="subtitle1"
                      gutterBottom
                      className="w-full"
                    >
                      <ViewTimelineIcon
                        style={{ cursor: 'pointer' }}
                        onClick={compose(
                          setSelectedTimeline,
                          always('Certificates') as any,
                        )}
                        color={
                          certificates.filter((x) => x.courseID === course?.id)
                            .length > 0
                            ? 'success'
                            : 'error'
                        }
                      />{' '}
                      Has Certificate:{' '}
                      {certificates.filter((x) => x.courseID === course?.id)
                        .length > 0
                        ? ' Yes'
                        : ' No'}
                    </Typography>
                    <Typography
                      variant="subtitle1"
                      gutterBottom
                      className="w-full"
                    >
                      <ViewTimelineIcon
                        style={{ cursor: 'pointer' }}
                        onClick={compose(
                          setSelectedTimeline,
                          always('CourseEvents') as any,
                        )}
                        color={
                          courseEvents.filter((x) => x.courseID === course?.id)
                            .length > 0
                            ? 'success'
                            : 'error'
                        }
                      />{' '}
                      Has Course Events:{' '}
                      {courseEvents.filter((x) => x.courseID === course?.id)
                        .length > 0
                        ? 'Yes'
                        : 'No'}
                    </Typography>
                    {classes.length === 1 && (
                      <Typography
                        variant="subtitle1"
                        gutterBottom
                        className="w-full"
                      >
                        <ViewTimelineIcon
                          style={{ cursor: 'pointer' }}
                          onClick={() => {
                            setSelectedClassID(classes?.[0].id)
                            setSelectedTimeline('QuizResults')
                          }}
                          color={
                            quizResults.filter(
                              (x) => x.classID === classes?.[0].id,
                            ).length > 0
                              ? 'success'
                              : 'error'
                          }
                        />{' '}
                        Has Taken Quiz:{' '}
                        {quizResults.filter(
                          (x) => x.classID === classes?.[0].id,
                        ).length > 0
                          ? 'Yes'
                          : 'No'}
                      </Typography>
                    )}
                  </Grid>
                </Grid>
                {classes.length > 1 && (
                  <Typography
                    variant="h5"
                    color="text.secondary"
                    gutterBottom
                    className="w-full"
                  >
                    Classes in this Course:
                  </Typography>
                )}
                {classes.length > 1 &&
                  classes?.map((c) => (
                    <Grid container>
                      <Grid xs={12} md={6}>
                        <Typography
                          variant="h6"
                          color="text.secondary"
                          gutterBottom
                          className="w-full"
                        >
                          {c?.name}
                        </Typography>
                        <Typography
                          variant="subtitle1"
                          gutterBottom
                          className="w-full"
                        >
                          <ViewTimelineIcon
                            style={{ cursor: 'pointer' }}
                            onClick={() => {
                              setSelectedClassID(c?.id)
                              setSelectedTimeline('ClassEvents')
                            }}
                            color={
                              classEvents.filter((x) => x.classID === c?.id)
                                .length > 0
                                ? 'success'
                                : 'error'
                            }
                          />{' '}
                          Has Class Events:{' '}
                          {classEvents.filter((x) => x.classID === c?.id)
                            .length > 0
                            ? 'Yes'
                            : 'No'}
                        </Typography>
                        <Typography
                          variant="subtitle1"
                          gutterBottom
                          className="w-full"
                        >
                          <ViewTimelineIcon
                            style={{ cursor: 'pointer' }}
                            onClick={() => {
                              setSelectedClassID(c?.id)
                              setSelectedTimeline('QuizResults')
                            }}
                            color={
                              quizResults.filter((x) => x.classID === c?.id)
                                .length > 0
                                ? 'success'
                                : 'error'
                            }
                          />{' '}
                          Has Taken Quiz:{' '}
                          {quizResults.filter((x) => x.classID === c?.id)
                            .length > 0
                            ? 'Yes'
                            : 'No'}
                        </Typography>
                        <Button
                          variant="outlined"
                          onClick={() => {
                            setSelectedClassID(c?.id)
                            setModalStatus('CompleteClass')
                          }}
                        >
                          Complete Class
                        </Button>
                      </Grid>
                    </Grid>
                  ))}
              </Paper>
              {selectedTimeline === 'Certificates' && (
                <>
                  <div style={timeLineStyle}>
                    <Typography variant="h5" align="center" className="py-4">
                      Certificate History
                    </Typography>
                    {certificates
                      .filter((x) => x.courseID === course?.id)
                      .map((certificate, i, arr) => (
                        <Timeline
                          key={certificate.id}
                          // @ts-ignore
                          placeholder={undefined}
                          onPointerEnterCapture={undefined}
                          onPointerLeaveCapture={undefined}
                        >
                          <TimelineItem>
                            <TimelineOppositeContent color="textSecondary">
                              {dayjs(certificate.createdTime).format(
                                'MM/DD/YYYY',
                              )}
                            </TimelineOppositeContent>
                            <TimelineSeparator>
                              <TimelineDot />
                              {arr.length - 1 !== i && <TimelineConnector />}
                            </TimelineSeparator>
                            <TimelineContent>
                              Acquired Certificate
                            </TimelineContent>
                          </TimelineItem>
                        </Timeline>
                      ))}
                  </div>
                </>
              )}
              {selectedTimeline === 'CourseEvents' && (
                <>
                  <div style={timeLineStyle}>
                    <Typography variant="h5" align="center" className="py-4">
                      Course Event History
                    </Typography>
                    {courseEvents
                      .filter((x) => x.courseID === course?.id)
                      .map((courseEvent, i, arr) => (
                        <Timeline
                          key={courseEvent.id}
                          // @ts-ignore
                          placeholder={undefined}
                          onPointerEnterCapture={undefined}
                          onPointerLeaveCapture={undefined}
                        >
                          <TimelineItem>
                            <TimelineOppositeContent color="textSecondary">
                              {dayjs(courseEvent.createdTime).format(
                                'MM/DD/YYYY',
                              )}
                            </TimelineOppositeContent>
                            <TimelineSeparator>
                              <TimelineDot />
                              {arr.length - 1 !== i && <TimelineConnector />}
                            </TimelineSeparator>
                            <TimelineContent>
                              Course {courseEvent.status}
                            </TimelineContent>
                          </TimelineItem>
                        </Timeline>
                      ))}
                  </div>
                </>
              )}
              {selectedTimeline === 'ClassEvents' && (
                <>
                  <div style={timeLineStyle}>
                    <Typography variant="h5" align="center" className="py-4">
                      Class Event History
                    </Typography>
                    {classEvents
                      .filter((x) => x.classID === selectedClassID)
                      .map((classEvent, i, arr) => (
                        <Timeline
                          key={classEvent.id}
                          // @ts-ignore
                          placeholder={undefined}
                          onPointerEnterCapture={undefined}
                          onPointerLeaveCapture={undefined}
                        >
                          <TimelineItem>
                            <TimelineOppositeContent color="textSecondary">
                              {dayjs(classEvent.createdTime).format(
                                'MM/DD/YYYY',
                              )}
                            </TimelineOppositeContent>
                            <TimelineSeparator>
                              <TimelineDot />
                              {arr.length - 1 !== i && <TimelineConnector />}
                            </TimelineSeparator>
                            <TimelineContent>
                              Class {classEvent.status}
                            </TimelineContent>
                          </TimelineItem>
                        </Timeline>
                      ))}
                  </div>
                </>
              )}
              {selectedTimeline === 'QuizResults' && (
                <>
                  <div style={timeLineStyle}>
                    <Typography variant="h5" align="center" className="py-4">
                      Quiz History
                    </Typography>
                    {quizResults
                      .filter((x) => x.classID === selectedClassID)
                      .map((qr, i, arr) => (
                        <Timeline
                          key={qr.id}
                          // @ts-ignore
                          placeholder={undefined}
                          onPointerEnterCapture={undefined}
                          onPointerLeaveCapture={undefined}
                        >
                          <TimelineItem>
                            <TimelineOppositeContent color="textSecondary">
                              {dayjs(qr.createdTime).format('MM/DD/YYYY')}
                            </TimelineOppositeContent>
                            <TimelineSeparator>
                              <TimelineDot />
                              {arr.length - 1 !== i && <TimelineConnector />}
                            </TimelineSeparator>
                            <TimelineContent>
                              Result: {qr.result} | Score: {qr.score}
                            </TimelineContent>
                          </TimelineItem>
                        </Timeline>
                      ))}
                  </div>
                </>
              )}
            </Grid>
          )}
        </Grid>
        <ConfirmCompleteClassForUser />
        <ConfirmCompleteVideoForUser />
        <Snackbar open={!!errorMessage} onClose={() => setErrorMessage('')}>
          <Alert
            variant="filled"
            onClose={() => setErrorMessage('')}
            severity="error"
          >
            <strong>Error with request: </strong>
            {errorMessage}
          </Alert>
        </Snackbar>
      </>
      <Box flexGrow={1}>
        <Subheader title="Videos" />
      </Box>
      <Grid container spacing={2}>
        <Grid xs={12} md={6}>
          <Autocomplete
            className="mx-auto my-4"
            isOptionEqualToValue={(option, value) => option.id === value.id}
            getOptionLabel={(option) => `${option.name}`}
            onChange={(_, value) => {
              setSelectedVideoID(value?.id ?? '')
            }}
            disablePortal
            options={videos}
            renderInput={(params) => (
              <TextField {...params} label="All Videos" />
            )}
          />
        </Grid>
        <Grid xs={12} md={6} display="flex" alignItems="center">
          {selectedVideoID && (
            <Button
              onClick={() => setModalStatus('CompleteVideo')}
              variant="contained"
            >
              Complete Video For User
            </Button>
          )}
        </Grid>
      </Grid>
    </>
  )
}
